/**
 * Where the chosen project per organisation is kept.
 *
 * Separate from `useSelectedProject` so the part with rules can be tested: the
 * hook is React plumbing, while this decides what a stored value means. It has
 * to survive whatever is actually in `localStorage`, which is not always what we
 * put there — a half-written entry, a value from an older shape, or a browser
 * that refuses storage entirely.
 *
 * **Keyed by organisation**, because a project id means nothing outside its org:
 * switching orgs must not carry a selection across, and switching back should
 * restore it.
 *
 * Every access is guarded. A remembered selection is a convenience; losing it is
 * a small annoyance, while throwing out of a render is a blank window — so a
 * storage failure always costs the former.
 */

const STORAGE_KEY = "bimgo_selected_project";

/** `{ [organizationId]: projectId }` */
export type Selections = Record<string, string>;

/** Somewhere to read and write strings — `localStorage`, or a stub in a test. */
export interface StringStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStore(): StringStore | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Touching `localStorage` itself throws where site data is blocked.
    return null;
  }
}

/**
 * A plain record of string→string, or `{}`.
 *
 * **Arrays are rejected**, not merely non-objects: `typeof [] === "object"` and
 * an array is not null, so the obvious guard lets one through, and spreading it
 * later would persist `{"0": …}`. Non-string values are dropped for the same
 * reason — a stored `{ org: 3 }` would otherwise become a project id of `3`.
 */
export function parseSelections(raw: string | null): Selections {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};

  const clean: Selections = {};
  for (const [org, project] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof project === "string" && project.length > 0) clean[org] = project;
  }
  return clean;
}

export function readSelections(store: StringStore | null = browserStore()): Selections {
  if (!store) return {};
  try {
    return parseSelections(store.getItem(STORAGE_KEY));
  } catch {
    return {};
  }
}

export function writeSelections(
  next: Selections,
  store: StringStore | null = browserStore(),
): void {
  if (!store) return;
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota, private mode, blocked site data — never fatal.
  }
}

/** The selection for one organisation, or null. */
export function selectionFor(selections: Selections, orgId: string | null): string | null {
  if (!orgId) return null;
  return selections[orgId] ?? null;
}

/** `selections` without `orgId`'s entry. Pure — does not persist. */
export function withoutSelection(selections: Selections, orgId: string): Selections {
  const next = { ...selections };
  delete next[orgId];
  return next;
}

/** `selections` with `orgId` pointing at `projectId`. Pure — does not persist. */
export function withSelection(
  selections: Selections,
  orgId: string,
  projectId: string,
): Selections {
  return { ...selections, [orgId]: projectId };
}

export const SELECTION_STORAGE_KEY = STORAGE_KEY;
